import { type } from "@/migrated-widgets/table-map/utils/types";
import { BaseService } from "../BaseService";
import { GetLocationDataPayload, GetLocationDataResponse, GetLocationDataReturn } from "./types";
import { Environments } from "@/migrated-widgets/table-map/constants/Environments";
import { GeoJSON } from "@/migrated-widgets/table-map/components/molecules/Map/types";

class PortalV2ServiceClass extends BaseService {
    constructor() {
        super(`${Environments.PORTAL_API_URL}/mapdata`);
    }

    async getLocationData({ token, userGuid, ...params }: GetLocationDataPayload): Promise<GetLocationDataReturn> {
        const fetchData = async (tmpData: GetLocationDataResponse[] = []): Promise<GetLocationDataResponse[]> => {
            const { data } = await this.axios.get<GetLocationDataResponse>("/getLocationData", {
                params,
                headers: {
                    Authorization: token,
                    userguid: userGuid,
                },
            });

            if (data.currentPage < data.totalPages) {
                params.page = data.currentPage + 1;

                return fetchData([...tmpData, data]);
            }

            return [...tmpData, data];
        };

        const pages = await fetchData();

        const data = pages.reduce((acc, curr) => {
            return {
                columnDefs: [...(acc?.columnDefs ?? []), ...(curr?.columnDefs ?? [])],
                currentPage: curr.currentPage,
                location: {
                    type: "FeatureCollection",
                    features: [...(acc?.location?.features ?? []), ...(curr?.location?.features ?? [])],
                } as GeoJSON,
                radius: {
                    type: "FeatureCollection",
                    features: [...(acc?.radius?.features ?? []), ...(curr?.radius?.features ?? [])],
                } as GeoJSON,
                raw: {
                    columnDefs: [...(acc.raw?.columnDefs ?? []), ...(curr.raw?.columnDefs ?? [])],
                    data: [...(acc?.raw?.data ?? []), ...(curr?.raw?.data ?? [])],
                },
                states: {
                    type: "FeatureCollection",
                    features: [...(acc?.states?.features ?? []), ...(curr?.states?.features ?? [])],
                } as GeoJSON,
                cities: {
                    type: "FeatureCollection",
                    features: [...(acc?.cities?.features ?? []), ...(curr?.cities?.features ?? [])],
                } as GeoJSON,
                zipRegion: {
                    type: "FeatureCollection",
                    features: [...(acc?.zipRegion?.features ?? []), ...(curr?.zipRegion?.features ?? [])],
                } as GeoJSON,
                totalPages: curr.totalPages,
            };
        }, {} as GetLocationDataResponse);

        const circleFeatures = data?.radius?.features ?? [];

        const circleMarkers = circleFeatures
            .filter((el) => el.geometry.type.toLowerCase() === "point")
            .map((item, index) => {
                return {
                    position: {
                        lat: type<number>(item.geometry.coordinates[1]),
                        lng: type<number>(item.geometry.coordinates[0]),
                    },
                    locations: item.locations,
                    properties: item.properties,
                    radius: type<number>(item.properties.radius ?? 1000),
                    label: String(item.properties.label),
                };
            });

        const geometryFeatures = [...data?.zipRegion?.features, ...data?.cities?.features, ...data?.states?.features];

        return { ...data, circleMarkers, geometryFeatures };
    }
}

const PortalV2Service = new PortalV2ServiceClass();
export { PortalV2Service };
